import { HttpBackend, HttpClient, HttpContext, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { LoggerService } from '../service/logger.service';

/**
 * Peticiones cuyo componente muestra el error dentro del formulario/modal: el interceptor
 * no agrega su toast (evita el aviso duplicado). 401/refresh y "server unavailable" siguen igual.
 */
export const INLINE_ERRORS = new HttpContextToken<boolean>(() => false);
export const inlineErrors = () => new HttpContext().set(INLINE_ERRORS, true);

// Un solo refresh en vuelo compartido entre todas las peticiones que reciban 401
// simultáneamente (p. ej. al cargar una página con varios requests): el endpoint
// tiene rate limit en el backend y no queremos condiciones de carrera.
let refreshInFlight: Observable<string> | null = null;

const refreshAccessToken = (http: HttpClient): Observable<string> => {
  if (!refreshInFlight) {
    refreshInFlight = http
      .get<{ access_token: string }>(`${environment.baseUrl}/auth/refresh-token`, { withCredentials: true })
      .pipe(
        map(({ access_token }) => {
          const raw = sessionStorage.getItem('sessionData');
          if (raw) {
            sessionStorage.setItem('sessionData', JSON.stringify({ ...JSON.parse(raw), access_token }));
          }
          return access_token;
        }),
        finalize(() => { refreshInFlight = null; }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
  }
  return refreshInFlight;
};

export const dataServiceInterceptor: HttpInterceptorFn = (req, next) => {
  const toastr = inject(ToastrService)
  const router = inject(Router)
  const logger = inject(LoggerService)
  // HttpBackend salta la cadena de interceptores: el refresh no debe interceptarse a sí mismo.
  const refreshHttp = new HttpClient(inject(HttpBackend))

  const redirectToLogin = () => {
    const returnUrl = router.url
    sessionStorage.removeItem('sessionData')
    router.navigate(['/login'], { queryParams: { returnUrl } })
    toastr.warning('Your session has expired. Please log in again.', 'Session Expired')
  }

  return next(req).pipe(
    catchError((error) => {
      // Login errors are handled directly by the component
      if (req.url.includes('/auth/login')) {
        return throwError(() => error);
      }

      if (error.status === 0) {
        logger.error('Server unavailable', { url: req.url, error })
        toastr.error('Backend server is not responding. Please notify your administrator.', 'Server Unavailable')
        return throwError(() => error)
      }

      const code: string = error.error?.code ?? error.error?.error?.code ?? ''
      // validate.middleware (express-validator) responde { errors: [{ msg }] } sin `code`
      const message: string = error.error?.message ?? error.error?.error?.message ?? error.error?.errors?.[0]?.msg ?? ''
      const isClientEmailLookup = req.url.includes('/clients/email/')
      const isCreateClientRequest = req.method === 'POST' && /\/clients\/?$/.test(req.url)

      logger.error(`HTTP ${error.status} ${code}`, { url: req.url, message, error })

      if (code !== 'UNAUTHORIZED' && req.context.get(INLINE_ERRORS)) {
        return throwError(() => error)
      }

      switch (code) {
        case 'UNAUTHORIZED': {
          if (req.url.includes('/auth/refresh-token')) {
            redirectToLogin()
            break
          }
          // Access token vencido (15 min): renovarlo en silencio con la cookie
          // httpOnly refresh_token (7 días) y reintentar la petición original.
          // Solo se expulsa al login si el refresh también falla.
          return refreshAccessToken(refreshHttp).pipe(
            catchError(() => {
              redirectToLogin()
              return throwError(() => error)
            }),
            switchMap((token) =>
              next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })).pipe(
                catchError((retryError) => {
                  if (retryError?.status === 401) redirectToLogin()
                  return throwError(() => retryError)
                })
              )
            )
          )
        }

        case 'FORBIDDEN':
          if (isClientEmailLookup || isCreateClientRequest) {
            return throwError(() => error)
          }
          toastr.error(message || 'Access denied', 'Access Denied')
          break

        case 'VALIDATION_ERROR':
          toastr.error(message || 'Please check your data and try again', 'Validation Error')
          break

        case 'NOT_FOUND':
        case 'ROUTE_NOT_FOUND':
          toastr.error(message || 'Resource not found', 'Not Found')
          break

        case 'CONFLICT':
          if (isCreateClientRequest) {
            return throwError(() => error)
          }
          toastr.error(message || 'Data conflict detected', 'Conflict')
          break

        case 'PAYMENT_FAILED':
          toastr.error(message || 'Payment processing failed', 'Payment Error')
          break

        case 'INTERNAL_ERROR':
        case 'EXTERNAL_SERVICE_ERROR':
          toastr.error('An internal error occurred. Please try again later.', 'Server Error')
          break

        default:
          toastr.error(message || 'An unexpected error occurred', 'Error')
      }

      return throwError(() => error)
    })
  )
}
